import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'dva'
import { Checkbox, Button, message, Empty } from 'antd'
import { CheckboxValueType } from 'antd/es/checkbox/Group'
import type { CheckboxChangeEvent } from 'antd/es/checkbox'
import { updateArray, mul } from '@sisyphe/utils'
import { useRequest } from '@sisyphe/hooks'
import { add } from '@sisyphe/utils'
import * as api from 'services/store'
import { SwaggerDTO1 } from 'services/store'
import { Card } from '..'
import './index.less'

interface CardTableProps {
  /**数据源 */
  data?: Array<SwaggerDTO1.购物车信息1>
  skuStocks?: Object
  // 删除回调
  deleteCallback?: Function
  /**结算 */
  onSettlement?: Function
}

export default function CardTable({
  data,
  skuStocks,
  deleteCallback,
  onSettlement,
}: CardTableProps) {
  const dispatch = useDispatch()

  const [list, setList] = useState<
    Array<SwaggerDTO1.购物车信息1 & { allSalePrice?: string | number }>
  >([])
  const [checkedList, setCheckedList] = useState<CheckboxValueType[]>([])
  const [indeterminate, setIndeterminate] = useState(false)
  const [checkAll, setCheckAll] = useState(false)

  // 购物车管理-选中
  const { run: chooseCartUsingPOST } = useRequest(
    async data => {
      await api.chooseCartUsingPOST(data)
    },
    { manual: true }
  )

  const onChange = (itemList: CheckboxValueType[]) => {
    // 选中
    if (itemList.length > checkedList.length) {
      let ids = itemList.filter(value => !checkedList.includes(value))
      chooseCartUsingPOST({ ids, status: 1 })
      // 取消
    } else {
      let ids = checkedList.filter(value => !itemList.includes(value))
      chooseCartUsingPOST({ ids, status: 0 })
    }

    setCheckedList(itemList)
    setIndeterminate(
      !!itemList.length &&
        itemList.length <
          data!.filter(c => !c.shelfStatus && !c?.stockStatus).length
    )
    setCheckAll(
      itemList.length ===
        data!.filter(c => !c.shelfStatus && !c?.stockStatus).length
    )
  }

  const onCheckAllChange = (e: CheckboxChangeEvent) => {
    if (!data?.length) return

    // 选中取消
    chooseCartUsingPOST({
      ids: data.map(c => c.id),
      status: e.target.checked ? 1 : 0,
    })
    const newCheckList = e.target.checked
      ? data.filter(c => !c.shelfStatus && !c?.stockStatus).map(c => c.id)
      : []
    setCheckedList(newCheckList)

    setIndeterminate(false)
    setCheckAll(e.target.checked)
  }

  // 购物车管理-数量编辑
  const { run: shopCartEditUsingPOST } = useRequest(
    async data => {
      await api.shopCartEditUsingPOST(data)
    },
    { manual: true }
  )

  const numChange = async (num, item) => {
    await shopCartEditUsingPOST({ id: item.id, num })
    const newList = updateArray(
      list,
      c => c.id === item.id,
      () => {
        return { ...item, num }
      }
    )

    setList([...newList])
  }

  // 拆分成正常商品列表和失效商品列表以及计算总价
  const [normalList, loseList, totalPrice] = useMemo(() => {
    let normalList: Array<SwaggerDTO1.购物车信息1> = []
    // 下架列表
    let shelfItems: Array<SwaggerDTO1.购物车信息1> = []
    // 失效列表
    let loseItems: Array<SwaggerDTO1.购物车信息1> = []

    let totalPrice: string | number = 0

    list.forEach(item => {
      // 计算价格
      item.allSalePrice = mul(item?.salePrice || 0, item?.num || 0)

      // 下架
      if (item.shelfStatus) {
        shelfItems.push(item)
        // 售罄
      } else if (item.stockStatus) {
        loseItems.push(item)
        // 正常
      } else {
        normalList.push(item)
      }

      // 计算总价
      if (checkedList.includes(item?.id)) {
        totalPrice = add(totalPrice, +item.allSalePrice, 2)
      }
    })

    return [normalList, [...shelfItems, ...loseItems], totalPrice]
  }, [list, checkedList])

  // 购物车管理-删除商品
  const { run: shopCartDelUsingPOST } = useRequest(
    async data => {
      await api.shopCartDelUsingPOST(data?.id)
      message?.success('操作成功')
      dispatch({ type: 'cart/getShopCartNum' })
      deleteCallback?.()
    },
    { manual: true }
  )

  // 结算
  const settlementHandle = () => {
    // 商品信息
    let spuInfos = list?.filter(item => checkedList.includes(item.id))
    const params = {
      spuInfos: spuInfos?.map(c => ({
        ...c,
        salesNum: c.num,
        skuNo: c.skuCode,
        spuCoverUrl: c.appImgUrl,
        spuNo: c.spuCode,
      })),
      orderAmount: totalPrice,
      remark: '',
    }
    onSettlement?.(params)
  }

  // 购物车管理-批量删除商品
  const { run: shopCartBatchDelUsingPOST } = useRequest(
    async data => {
      await api.shopCartBatchDelUsingPOST(data)
      setCheckedList([])
      setIndeterminate(false)
      setCheckAll(false)
      message?.success('操作成功')
      deleteCallback?.()
      dispatch({ type: 'cart/getShopCartNum' })
    },
    { manual: true }
  )

  // 批量删除
  const batchDelete = () => {
    if (!checkedList.length) {
      return message.warning('请选择商品')
    }
    const ids = list.map(c => c.id)
    shopCartBatchDelUsingPOST({ ids })
  }

  useEffect(() => {
    setList(data || [])

    const selectProducts =
      data?.filter(c => !c.shelfStatus && !c?.stockStatus) || []
    const itemList = selectProducts?.map(c => c.id)

    setCheckedList(itemList)
    setIndeterminate(
      !!itemList.length &&
        itemList.length <
          data!.filter(c => !c.shelfStatus && !c?.stockStatus).length
    )
    setCheckAll(
      itemList?.length ===
        data?.filter(c => !c.shelfStatus && !c?.stockStatus)?.length
    )
  }, [data])

  return (
    <div className="card-table">
      <div className="cart-thead">
        <div className="cart-thead-check-all">
          <Checkbox
            indeterminate={indeterminate}
            onChange={onCheckAllChange}
            checked={checkAll}
          >
            全选
          </Checkbox>
        </div>
        <div className="cart-thead-info">商品信息</div>
        <div className="cart-thead-unit-price">单价（元）</div>
        <div className="cart-thead-amount">数量</div>
        <div className="cart-thead-price">金额（元）</div>
        <div className="cart-thead-options">操作</div>
      </div>
      <Checkbox.Group value={checkedList} onChange={onChange}>
        <div className="cart-tbody">
          {normalList?.map(item => (
            <Card
              key={item.id}
              data={item}
              skuStocks={skuStocks}
              numChange={e => numChange(e, item)}
              onDelete={shopCartDelUsingPOST}
            />
          ))}
          {!!loseList?.length && <p className="cart-tbody-title">失效商品</p>}
          {loseList?.map(item => (
            <Card
              lose={item.shelfStatus || item?.stockStatus}
              key={item.id}
              data={item}
              skuStocks={skuStocks}
              numChange={e => numChange(e, item)}
              onDelete={shopCartDelUsingPOST}
            />
          ))}
          {!list.length && (
            <div className="nothing">
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
            </div>
          )}
        </div>
      </Checkbox.Group>
      <div className="card-footer">
        <div className="card-footer-cont">
          <div className="card-footer-cont-left">
            <Checkbox
              indeterminate={indeterminate}
              onChange={onCheckAllChange}
              checked={checkAll}
            >
              全选
            </Checkbox>
            <Button
              type="link"
              size="small"
              className="card-footer-delete"
              onClick={batchDelete}
            >
              删除选中商品
            </Button>
          </div>
          <div className="card-footer-cont-right">
            <p className="card-footer-cont-total">
              合计：<span>¥{totalPrice}</span>
            </p>
            <Button
              type="primary"
              className="card-footer-settlement"
              onClick={settlementHandle}
            >
              结算
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
